import { Injectable } from '@angular/core';
import { MixpanelService } from '@core/services';
import { DateHelper } from '@shared/helpers';
import { Assay } from '@shared/interfaces';

@Injectable({ providedIn: 'root' })
export class CkmMixpanelLogsService {
    private readonly prefix = 'ckm-';

    submitRetestTimeSpent: { start: Date | null; end: Date | null } = { start: null, end: null };
    submitInvestigationTimeSpent: { start: Date | null; end: Date | null } = { start: null, end: null };
    searchWellplateCount: number = 0;

    constructor(private mixpanel: MixpanelService) {}

    logTimeSpentForSubmitRetest(): void {
        if (!this.submitRetestTimeSpent || !this.submitRetestTimeSpent.start || !this.submitRetestTimeSpent.end) {
            return;
        }

        const timeSpent = DateHelper.millisToMinutesAndSeconds(
            this.submitRetestTimeSpent.end.getTime() - this.submitRetestTimeSpent.start.getTime()
        );

        this.mixpanel.logFormSubmit(`${this.prefix}retest-submit-duration: ${timeSpent}`);
    }

    logTimeSpentForSubmitInvestigation(): void {
        if (
            !this.submitInvestigationTimeSpent ||
            !this.submitInvestigationTimeSpent.start ||
            !this.submitInvestigationTimeSpent.end
        ) {
            return;
        }

        const timeSpent = DateHelper.millisToMinutesAndSeconds(
            this.submitInvestigationTimeSpent.end.getTime() - this.submitInvestigationTimeSpent.start.getTime()
        );

        this.mixpanel.logFormSubmit(`${this.prefix}investigation-submit-duration: ${timeSpent}`);
    }

    logSearchWellplateCount(): void {
        this.mixpanel.logEvent(`${this.prefix}searched-wellplates-in-one-sitting: ${this.searchWellplateCount}`);
    }

    logQuestionnaireAnswer(currentStepId: number, answer: string): void {
        if (currentStepId === 1) {
            this.mixpanel.logEvent(`${this.prefix}retest-select-reason: ${answer}`);
            return;
        }

        if (currentStepId === 10) {
            this.mixpanel.logEvent(`${this.prefix}investigation-select-reason: ${answer}`);
            return;
        }

        if (currentStepId === 40) {
            this.mixpanel.logEvent(`${this.prefix}investigation - Would you like to retest your samples? ${answer}`);
            return;
        }

        if (currentStepId === 50) {
            this.mixpanel.logEvent(`${this.prefix}investigation - How long ago did you submit a retest? ${answer}`);
            return;
        }
    }

    logSamplesAndAssaysSearch(searchBy: string): void {
        this.mixpanel.logEvent(`${this.prefix}samples-and-assays-search`, { searchBy });
    }

    logSamplesAndAssaysExpandAll(): void {
        this.mixpanel.logEvent(`${this.prefix}samples-and-assays-toggle-expand-all`);
    }

    logSamplesAndAssaysSelectAll(): void {
        this.mixpanel.logEvent(`${this.prefix}samples-and-assays-toggle-select-all`);
    }

    logSamplesAndAssaysAssayChange(assays: Assay[]): void {
        this.mixpanel.logEvent(`${this.prefix}samples-and-assays-change-assays`, { assays: assays.map(a => a.name) });
    }

    logSamplesAndAssaysViewResults(resultsUrl: string): void {
        this.mixpanel.logEvent(`${this.prefix}samples-and-assays-view-results`, { results_link: resultsUrl });
    }

    logQuestionnaireSelectPreviousStep(ckmType: string): void {
        this.mixpanel.logEvent(`${this.prefix}questionnaire-${ckmType}-select-previous-step`);
    }

    logQuestionnaireSelectedAssay(selectedAssay: any): void {
        this.mixpanel.logEvent(`${this.prefix}questionnaire-selected-assay: ${selectedAssay.text}`);
    }

    logQuestionnaireSelectedStrain(selectedStrain: any): void {
        this.mixpanel.logEvent(`${this.prefix}questionnaire-selected-strain: ${selectedStrain.text}`);
    }

    logQuestionnaireStartRetestFromInvestigation(): void {
        this.mixpanel.logEvent(`${this.prefix}questionnaire-start-retest-from-investigation`);
    }

    logQuestionnaireRedirectModalContinueInvestigation(): void {
        this.mixpanel.logEvent(`${this.prefix}questionnaire-cancel-begin-retest-from-investigation`);
    }

    startTimerForSubmitRetest(): void {
        this.submitRetestTimeSpent.start = new Date();
    }

    endTimerForSubmitRetest(): void {
        this.submitRetestTimeSpent.end = new Date();
    }

    startTimerForSubmitInvestigation(): void {
        this.submitInvestigationTimeSpent.start = new Date();
    }

    endTimerForSubmitInvestigation(): void {
        this.submitInvestigationTimeSpent.end = new Date();
    }

    resetSearchWellplateCount(): void {
        this.searchWellplateCount = 0;
    }

    incrementSearchWellplateCount(): void {
        this.searchWellplateCount += 1;
    }

    logHelpAndSupportNavButton(): void {
        this.mixpanel.logEvent(`${this.prefix}open-help-and-support-overlay`);
    }

    logClickCheckMateButtonFromDashboard(): void {
        this.mixpanel.logEvent(`${this.prefix}click-CheckMate-from-dashboard`);
    }

    logClickSubmitRetestFromDashboard(): void {
        this.mixpanel.logEvent(`${this.prefix}click-submit-retest-from-dashboard`);
    }

    logClickStartInvestigationFromDashboard(): void {
        this.mixpanel.logEvent(`${this.prefix}click-start-investigation-from-dashboard`);
    }

    logClickStrainLinkFromCkmListing(strainUrl: string): void {
        this.mixpanel.logEvent(`${this.prefix}click-strain-link`, {
            strain_link: strainUrl,
        });
    }

    logClickOnHoldInquiryFromCkmListing(inquiryId: number): void {
        this.mixpanel.logEvent(`${this.prefix}click-on-hold-inquiry`, {
            inquiry_id: inquiryId,
        });
    }

    logCkmListingExportPDF(): void {
        this.mixpanel.logEvent(`${this.prefix}listing-export-PDF`);
    }

    logCkmListingExportCSV(): void {
        this.mixpanel.logEvent(`${this.prefix}listing-export-CSV`);
    }

    logCkmListingPaginationChange(): void {
        this.mixpanel.logEvent(`${this.prefix}listing-pagination-change`);
    }

    logCkmListingSearchValueChange(): void {
        this.mixpanel.logEvent(`${this.prefix}listing-search-value-change`);
    }

    logCkmListingStatusFilterChange(): void {
        this.mixpanel.logEvent(`${this.prefix}listing-status-filter-change`);
    }

    logCkmListingApplyFilterChange(): void {
        this.mixpanel.logEvent(`${this.prefix}listing-applied-filters-change`);
    }

    logCkmListingApplySortChange(): void {
        this.mixpanel.logEvent(`${this.prefix}listing-applied-sort-change`);
    }

    logCkmListingResetSort(): void {
        this.mixpanel.logEvent(`${this.prefix}listing-reset-sort`);
    }

    logCkmListingResetFilters(): void {
        this.mixpanel.logEvent(`${this.prefix}listing-reset-filters`);
    }

    logViewResults(inquiryId: number): void {
        this.mixpanel.logEvent(`${this.prefix}details-view-results`, { inquiry_id: inquiryId });
    }

    logLeaveCkmDetailsPage(): void {
        this.mixpanel.logEvent(`${this.prefix}details-leave-page`);
    }

    logStartInvestigationFromRetest(inquiryId: number): void {
        this.mixpanel.logEvent(`${this.prefix}details-start-investigation-from-retest`, { inquiry_id: inquiryId });
    }

    logDetailsExportCSV(inquiryId: number): void {
        this.mixpanel.logEvent(`${this.prefix}details-export-CSV`, { inquiry_id: inquiryId });
    }

    logLeavePageOnUnsavedChangesModal(mixpanelKey: string): void {
        this.mixpanel.logEvent(mixpanelKey);
    }

    logOpenEditRetestDetailsModal(): void {
        this.mixpanel.logOpenModal(`${this.prefix}retest-open-edit-ckm-samples-and-assays-modal`);
    }

    logSaveEditRetestDetails(): void {
        this.mixpanel.logEvent(`${this.prefix}save-edit-samples-and-assays`);
    }

    logRetestSetupSelectRequester(): void {
        this.mixpanel.logEvent(`${this.prefix}retest-select-requester`);
    }

    logRetestSetupSelectInformedUser(): void {
        this.mixpanel.logEvent(`${this.prefix}retest-select-informed-user`);
    }

    logRetestSetupAddNewInformedUser(): void {
        this.mixpanel.logFormSubmit(`${this.prefix}retest-add-informed-user`);
    }

    logRetestSummaryRemoveStrain(): void {
        this.mixpanel.logEvent(`${this.prefix}ckm-retest-summary-remove-strain`);
    }
}
